import React, { useState, useEffect, useRef } from 'react';
import { database } from '../firebase';
import { ref, onValue, push, serverTimestamp } from 'firebase/database';
import { UserAdapter } from '../adapters/UserAdapter';
import { encodeIP } from '../utils/ipUtils';
import '../styles/Terminal.css';
import { AIAdapter } from '../adapters/AIAdapter';

const COMMANDS = {
  help: {
    description: 'Show terminal usage information',
    handler: (setMessages) => {
      setMessages(prev => [...prev, {
        text: "Terminal Usage:\n- Type normally to chat with other humans\n- Use @judex to get a response from the AI",
        timestamp: Date.now(),
        type: 'system',
        id: `system-${Date.now()}`
      }]);
    }
  },
  search: {
    description: 'Search chat history',
    handler: async (setMessages, args) => {
      if (!args.length) {
        setMessages(prev => [...prev, {
          text: 'Usage: /search <query>',
          timestamp: Date.now(),
          type: 'system',
          id: `system-${Date.now()}`
        }]);
        return;
      }
      try {
        const response = await fetch(`/api/chat/search?query=${encodeURIComponent(args.join(' '))}`);
        const data = await response.json();
        const results = data.results.map(r => `${r.text} (Score: ${r.relevanceScore.toFixed(2)})`).join('\n');
        setMessages(prev => [...prev, {
          text: `Search Results:\n${results || 'No results found'}`,
          timestamp: Date.now(),
          type: 'system',
          id: `system-${Date.now()}`
        }]);
      } catch (error) {
        console.error('Search error:', error);
        setMessages(prev => [...prev, {
          text: 'Error searching chat history',
          timestamp: Date.now(),
          type: 'error',
          id: `error-${Date.now()}`
        }]);
      }
    }
  },
  clear: {
    description: 'Clear terminal',
    handler: (setMessages) => {
      setMessages([]);
    }
  }
};

function Terminal() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Subscribe to Firebase real-time updates
  useEffect(() => {
    const chatRef = ref(database, 'chat');
    const unsubscribe = onValue(chatRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messageList = Object.entries(data).map(([key, value]) => ({
          ...value,
          id: key
        }));
        setMessages(messageList);
      }
    });

    return () => unsubscribe();
  }, []);

  // Update conversation history for AI context
  const updateConversationHistory = (userMsg, aiMsg = null) => {
    setConversationHistory(prev => {
      // Create new history with user message
      const updated = [...prev, { role: 'user', content: userMsg }];
      
      // Add AI message if provided
      if (aiMsg) {
        updated.push({ role: 'assistant', content: aiMsg });
      }
      
      // Keep only the last 10 messages (5 exchanges)
      return updated.slice(-10);
    });
  };

  const handleCommand = async (command, args) => {
    const cmd = COMMANDS[command];
    if (cmd) {
      await cmd.handler(setMessages, args);
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const messageText = input.trim();
    if (!messageText) return;

    try {
        setInput('');
        
        // Check if message is a command
        if (messageText.startsWith('/')) {
          const parts = messageText.slice(1).split(' ');
          const command = parts[0];
          const args = parts.slice(1);
          
          // Handle the command
          const handled = await handleCommand(command, args);
          
          // If command was handled, don't send to AI
          if (handled) return;
        }
        
        // Add user message to Firebase
        const chatRef = ref(database, 'chat');
        const userMessage = {
            text: messageText,
            timestamp: Date.now(),
            type: 'user',
            sender: 'human',
        };
        await push(chatRef, userMessage);

        // Only send to AI if the message contains "@judex"
        if (messageText.toLowerCase().includes("@judex")) {
          // Extract the actual message without the @judex mention
          const cleanMessage = messageText.replace(/@judex/gi, '').trim();
          
          // Update conversation history with this user message
          updateConversationHistory(cleanMessage);
          
          // Get user context
          const userContext = await UserAdapter.getUserContext();
          
          // Send message to AI with user context and conversation history
          const { response, timestamp } = await AIAdapter.sendMessage(
            messageText, 
            userContext,
            conversationHistory
          );
          
          // Add AI response to Firebase - the onValue listener will handle updating the UI
          const aiMessage = {
              text: response,
              timestamp: timestamp || Date.now(),
              type: 'ai',
              sender: 'judex'
          };
          await push(chatRef, aiMessage);
          
          // Update conversation history with AI response
          updateConversationHistory(cleanMessage, response);
        }

    } catch (error) {
        console.error('Error in chat:', error);
        // Add error message to local state
        setMessages(prev => [...prev, {
            text: 'Sorry, I\'m having trouble connecting. Please try again later.',
            timestamp: Date.now(),
            type: 'error',
            id: `error-${Date.now()}`
        }]);
    }
  };

  return (
    <div className={`terminal-container ${isVisible ? 'visible' : ''}`}>
      <button 
        className="terminal-button"
        onClick={() => setIsVisible(!isVisible)}
      >
        {isVisible ? 'HIDE' : 'SHOW'} TERMINAL
      </button>
      
      {isVisible && (
        <>
          <div className="terminal-header">
            <span className="terminal-title">Global TERMINAL</span>
          </div>

          <div 
            className="terminal-messages" 
            ref={messagesContainerRef}
          >
            {messages.map((msg) => (
              <div key={msg.id} className={`message ${msg.type}`}>
                <span className="prompt">
                  {msg.type === 'ai' ? 'judex ~$' : 
                   msg.type === 'system' ? 'system ~$' : 
                   'human_testsubject ~$'}
                </span> 
                <span className="message-text">{msg.text}</span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="terminal-input-container">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="terminal-input"
              placeholder="Type your message or /help for commands..."
            />
          </form>
        </>
      )}
    </div>
  );
}

export default Terminal; 